import { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { EnvelopeFill, Github, Linkedin } from 'react-bootstrap-icons';

const credibility = [
  '3+ years building production React systems',
  'Operational workflow products for editorial and analytics teams',
  'Architecture-minded implementation with measurable outcomes',
];

const systems = [
  {
    name: 'StackScope',
    tagline: 'Developer Stack Monitoring & Engineering Analytics Platform',
    summary:
      'Monitors repository activity and converts raw engineering events into actionable dashboard insights for productivity and workflow health.',
    impact: ['Repository visibility across commits, PRs, and issues', 'Workflow bottleneck identification', 'Team-level engineering trend tracking'],
    stack: ['React', 'Node.js', 'GitHub API', 'OpenAI API'],
    slides: [
      { src: '/stackscope1.png', label: 'Landing Experience' },
      { src: '/stackscope2.png', label: 'Dashboard Integration View' },
      { src: '/stackscope3.png', label: 'Engineering Insight View' },
      { src: '/stackscope4.png', label: 'AI Summary Panel' },
    ],
  },
  {
    name: 'NewsroomOps UI',
    tagline: 'Editorial Workflow Interface System',
    summary:
      'Structured interface for article lifecycle orchestration from ingestion through review, scheduling, and publishing.',
    impact: ['Operational lifecycle tracking', 'Editorial workflow visibility', 'Cross-team publishing coordination'],
    stack: ['React', 'TypeScript', 'REST APIs'],
    slides: [
      { src: '/Newsroom1.png', label: 'Content Queue View' },
      { src: '/Newsroom2.png', label: 'Article Side Panel' },
      { src: '/Newsroom3.png', label: 'Schedule Publish Modal' },
      { src: '/Newsroom4.png', label: 'Editorial Detail Workflow' },
    ],
  },
  {
    name: 'DocReviewPro',
    tagline: 'Document Review & Analysis Platform',
    summary:
      'AI-assisted document review product that streamlines upload, parsing, extraction, and issue-focused evaluation workflows.',
    impact: ['Structured review workflow', 'Faster information extraction', 'Improved reviewer consistency'],
    stack: ['React', 'Node.js', 'OpenAI API'],
    slides: [
      { src: '/Docreview.png', label: 'Landing Workspace' },
      { src: '/Docreview1.png', label: 'Document Explorer' },
      { src: '/Docreview2.png', label: 'Review Workspace' },
      { src: '/Docreview3.png', label: 'Issue Resolution View' },
    ],
  },
];

const caseStudyDocs = [
  {
    title: 'Case Study 01 — Why AI-Generated Code Breaks Production Reliability',
    blurb:
      'AI coding tools can generate functional implementations quickly, but production failures emerge from hidden assumptions in input structures, concurrency behavior, state transitions, and failure handling. This study shows how teams improve reliability through explicit contracts, boundary validation, and failure-aware architecture.',
    file: '/Casestudy1.pptx',
  },
  {
    title: 'Case Study 02 — The Invisible Assumptions in AI-Generated Systems',
    blurb:
      'AI-assisted development accelerates delivery, but generated code often assumes stable API contracts, schema consistency, and predictable service interactions. This study explains how mature teams expose these assumptions using contract tests, schema validation, strong typing, and observability.',
    file: '/Casestudy2.pptx',
  },
];

const experience = [
  {
    company: 'India News',
    role: 'Frontend Engineer',
    period: '2023 — 2026',
    domain: 'Editorial Operations Platform',
    scope: ['React + TypeScript', 'REST API integration', 'Workflow dashboard UX'],
    bullets: [
      'Built internal newsroom workflow interfaces for ingestion, review, scheduling, and publishing',
      'Implemented robust API-state UX (loading, error, empty) across lifecycle views',
      'Partnered with editorial stakeholders to reduce workflow friction and improve release confidence',
    ],
    outcomes: ['~40% reduction in stale-data issues', '~35% fewer UI operational issues', '~25% fewer manual workflow steps'],
  },
  {
    company: 'Reliable Networks',
    role: 'IT Support Specialist',
    period: '2020 — 2021',
    domain: 'Infrastructure & Support Operations',
    scope: ['Root cause analysis', 'Incident response', 'Troubleshooting documentation'],
    bullets: [
      'Diagnosed OS, application, and network incidents across internal systems',
      'Standardized troubleshooting playbooks for faster and more consistent resolution',
    ],
    outcomes: ['Improved support turnaround time', 'Reduced repeat incident volume'],
  },
];

const education = [
  {
    degree: 'Postgraduate Degree — Web Development',
    school: 'Conestoga College',
    location: 'Ontario, Canada',
    period: '2021 — 2022',
  },
  {
    degree: 'Bachelor of Engineering — Computer Engineering',
    school: 'L.D. College of Engineering (GTU)',
    location: 'Ahmedabad, India',
    period: '2016 — 2020',
  },
];

const contacts = [
  {
    label: 'Email',
    value: 'harshal.patel146@gmail.com',
    href: 'mailto:harshal.patel146@gmail.com',
    icon: 'email',
  },
  {
    label: 'LinkedIn',
    value: 'linkedin.com/harshal-patel-650702135',
    href: 'https://linkedin.com/in/harshal-patel-650702135',
    icon: 'linkedin',
  },
  {
    label: 'GitHub',
    value: 'github.com/kookaboora',
    href: 'https://github.com/kookaboora',
    icon: 'github',
  },
];

const ContactLogo = ({ icon }) => {
  if (icon === 'email') return <EnvelopeFill size={18} />;
  if (icon === 'linkedin') return <Linkedin size={18} />;
  return <Github size={18} />;
};

const CaseStudySlider = ({ name, slides }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, 3500);

    return () => clearInterval(timer);
  }, [slides.length]);

  const next = () => setIndex((current) => (current + 1) % slides.length);
  const prev = () => setIndex((current) => (current - 1 + slides.length) % slides.length);

  const activeSlide = slides[index];

  return (
    <div className="case-slider">
      <div className="case-slider-frame">
        <img src={activeSlide.src} alt={`${name} - ${activeSlide.label}`} className="case-slide-image" />
        <div className="case-slider-caption">{activeSlide.label}</div>
      </div>
      <div className="case-slider-controls">
        <button type="button" onClick={prev} aria-label={`Previous ${name} image`}>←</button>
        <div className="case-slider-dots">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.src}
              type="button"
              className={slideIndex === index ? 'active' : ''}
              onClick={() => setIndex(slideIndex)}
              aria-label={`${name} slide ${slideIndex + 1}`}
            />
          ))}
        </div>
        <button type="button" onClick={next} aria-label={`Next ${name} image`}>→</button>
      </div>
    </div>
  );
};

export const EngineeringPlatform = () => {
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(caseStudyDocs[0]);

  const caseStudyEmbedSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(`https://harshalbuilds.dev${selectedCaseStudy.file}`)}`;

  return (
  <main>
    <section id="home" className="hero-section">
      <Container>
        <div className="hero-layout">
          <div>
            <p className="eyebrow">Software Engineer • Systems Portfolio</p>
            <h1>Building scalable web systems, operational platforms, and data-driven products.</h1>
            <p className="intro">
              Frontend engineer with strong system thinking, product ownership, and architecture-focused execution for internal tools and
              analytics experiences.
            </p>
            <div className="hero-action-row">
              <a href="/Harshal_Patel_Resume_Korea.pdf" className="hero-resume-btn" download>
                Download Resume
              </a>
              <button type="button" className="hero-print-btn" onClick={() => window.print()}>
                Download Portfolio Printout
              </button>
            </div>

            <div className="credibility-grid">
              {credibility.map((item) => (
                <article key={item} className="panel highlighted-panel">
                  <p>{item}</p>
                </article>
              ))}
            </div>
          </div>
          <div className="headshot-wrap">
            <img src="/headshot.jpeg" alt="Harshal Patel headshot" className="headshot" />
          </div>
        </div>
      </Container>
    </section>

    <section id="systems" className="content-section">
      <Container>
        <h2>Featured Systems</h2>
        <div className="systems-grid">
          {systems.map((system) => (
            <article key={system.name} className="panel system-card">
              <h3>{system.name}</h3>
              <p className="system-tagline">{system.tagline}</p>
              <CaseStudySlider name={system.name} slides={system.slides} />
              <p>{system.summary}</p>
              <h4>Key Outcomes</h4>
              <ul>
                {system.impact.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <h4>Stack</h4>
              <p>{system.stack.join(' • ')}</p>
            </article>
          ))}
        </div>
      </Container>
    </section>


    <section id="case-studies" className="content-section">
      <Container>
        <h2>Case Studies</h2>
        <div className="case-study-doc-grid">
          {caseStudyDocs.map((doc) => (
            <button
              key={doc.title}
              type="button"
              className={doc.title === selectedCaseStudy.title ? 'case-doc-card active' : 'case-doc-card'}
              onClick={() => setSelectedCaseStudy(doc)}
            >
              <h3>{doc.title}</h3>
              <p>{doc.blurb}</p>
              <span>Open in embedded PPT viewer</span>
            </button>
          ))}
        </div>

        <article className="panel case-study-viewer-panel">
          <div className="case-study-viewer-header">
            <h3>{selectedCaseStudy.title}</h3>
            <a href={selectedCaseStudy.file} target="_blank" rel="noreferrer">
              Download / Open PPT
            </a>
          </div>
          <iframe
            title={`${selectedCaseStudy.title} PPT`}
            src={caseStudyEmbedSrc}
            className="case-study-ppt-viewer"
          />
        </article>
      </Container>
    </section>

    <section id="experience" className="content-section">
      <Container>
        <h2>Experience</h2>
        <div className="experience-grid">
          {experience.map((job) => (
            <article key={job.company} className="panel experience-card">
              <div className="experience-header">
                <div>
                  <h3>{job.company}</h3>
                  <p className="experience-role"><strong>{job.role}</strong> • {job.period}</p>
                </div>
                <span className="experience-domain">{job.domain}</span>
              </div>

              <div className="experience-chips">
                {job.scope.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>

              <h4>Engineering Contributions</h4>
              <ul>
                {job.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              <h4>Outcomes</h4>
              <div className="experience-outcomes">
                {job.outcomes.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </Container>
    </section>

    <section id="education" className="content-section">
      <Container>
        <h2>Education</h2>
        <div className="education-grid">
          {education.map((item, index) => (
            <article key={item.degree} className="education-card panel">
              <div className="education-badge">0{index + 1}</div>
              <p className="education-period">{item.period}</p>
              <h3>{item.degree}</h3>
              <p className="education-school">{item.school}</p>
              <p>{item.location}</p>
            </article>
          ))}
        </div>
      </Container>
    </section>

    <section id="contact" className="content-section contact-section">
      <Container>
        <h2>Contact</h2>
        <article className="panel">
          <div className="contact-icon-grid">
            {contacts.map((contact) => (
              <a key={contact.label} href={contact.href} className="contact-icon-link" target={contact.label === 'Email' ? undefined : '_blank'} rel="noreferrer">
                <span className="contact-icon" aria-hidden="true"><ContactLogo icon={contact.icon} /></span>
                <span>
                  <strong>{contact.label}</strong>
                  <small>{contact.value}</small>
                </span>
              </a>
            ))}
          </div>
        </article>
      </Container>
    </section>
  </main>

  );
};
